import { isEmpty, isNil, isString } from 'lodash';
import React, { ReactNode, useContext, useMemo } from 'react';
import { View } from '@shared/components/View';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { IconButtonV2 } from '@shared/components/button';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { HoverTooltip } from '@shared/components/tooltip';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import {
  getComponentWithOverrides,
  isComponentHidden as getIsComponentHidden,
} from '@shared/data/cards/utils';
import { Entity } from '@shared/data/entity/Entity';
import { TagField } from '@shared/data/entity/fields/TagField';
import { LayoutMode, useLayoutMode } from '@shared/scenes/channel/LayoutMode';
import { useUpdateWorkflowEntity } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import {
  ChecklistTagComponent,
  getComponentIcon,
  getComponentLabel,
  isChecklistTagComponent,
  isInputComponent,
  isManagingInputComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { CollectionViewAnalytics } from '../../collectionView/CollectionViewAnalytics';
import {
  CollectionViewCard,
  CollectionViewCardStyles,
} from '../../collectionView/components/CollectionViewCard';
import {
  CardCollectionViewField,
  FieldStyles,
} from '../../collectionView/components/fields/CardCollectionViewField';
import { OpenEntityHandler } from '../../collectionView/hooks/useCollectionViewData';
import CardStyles from './Card.module.css';
import {
  headerFieldStyles,
  narrowCardFieldStyles,
  wideCardFieldStyles,
} from './listViewCardStyles';

export interface CardProps {
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplate: API.WorkflowTemplateFragment;
  entity: Entity;
  openEntity?: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  canUpdateCard?: boolean;
  analytics?: CollectionViewAnalytics;
  isRedacted?: boolean;
  hasOverflowMenu?: boolean;
  cardStyles?: CollectionViewCardStyles;
  testID?: string;
}

export function Card({
  viewTemplate,
  workflowTemplate,
  openEntity,
  getIsCardBeforePaidCutoff,
  entity,
  analytics,
  canUpdateCard = false,
  isRedacted = false,
  hasOverflowMenu = true,
  cardStyles,
  testID,
}: CardProps) {
  const { mode } = useLayoutMode();
  const { subtype } = viewTemplate;
  const isWideCardLayout = getIsWideCardLayout({ mode, subtype: subtype ?? null });
  const [firstComponent, otherComponents] = useVisibleComponents({
    workflowTemplate,
    viewTemplate,
    entity,
  });

  const onPress = () => {
    openEntity?.(entity.id, entity.navigationViewTemplateId);
  };

  const checklistComponent = getChecklistComponent({ viewTemplate, workflowTemplate });

  return (
    <View flex testID={testID} className={CardStyles.card}>
      <MBTouchableOpacity onPress={onPress} activeOpacity={0.9}>
        <CollectionViewCard
          workflowTemplate={workflowTemplate}
          entity={entity}
          openEntity={openEntity}
          getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
          hasOverflowMenu={hasOverflowMenu}
          cardStyles={cardStyles}
        >
          <RedactedWrapper isRedacted={isRedacted}>
            <CheckListWrapper entity={entity} checklistComponent={checklistComponent}>
              {!isNil(firstComponent) && (
                <CardCollectionViewComponent
                  component={firstComponent}
                  entity={entity}
                  workflowTemplateId={workflowTemplate.id}
                  fieldStyles={headerFieldStyles}
                  showLabel={isRedacted ? false : !viewTemplate.defaultHiddenLabels}
                  analytics={analytics}
                  canUpdateCard={canUpdateCard}
                  isRedacted={isRedacted}
                  isCollapsed={!isWideCardLayout}
                />
              )}
              <View row={isWideCardLayout} wrap={isWideCardLayout}>
                {!isRedacted &&
                  otherComponents.map(component => {
                    return (
                      <CardCollectionViewComponent
                        key={component.id}
                        component={component}
                        entity={entity}
                        workflowTemplateId={workflowTemplate.id}
                        fieldStyles={isWideCardLayout ? wideCardFieldStyles : narrowCardFieldStyles}
                        showLabel={!viewTemplate.defaultHiddenLabels}
                        analytics={analytics}
                        canUpdateCard={canUpdateCard}
                        isRedacted={false}
                        isCollapsed={!isWideCardLayout}
                      />
                    );
                  })}
                {isRedacted && <View className={CardStyles.redactedPlaceholder} />}
              </View>
            </CheckListWrapper>
          </RedactedWrapper>
        </CollectionViewCard>
      </MBTouchableOpacity>
    </View>
  );
}

function RedactedWrapper({
  children,
  isRedacted,
}: {
  children: React.ReactNode;
  isRedacted: boolean;
}) {
  return <View className={isRedacted && CardStyles.redacted}>{children}</View>;
}

function CheckListWrapper({
  children,
  entity,
  checklistComponent,
}: {
  children: ReactNode;
  entity: Entity;
  checklistComponent?: ChecklistTagComponent;
}) {
  if (isNil(checklistComponent)) {
    return <>{children}</>;
  } else {
    return (
      <View row className={CardStyles.checklistWrapper}>
        <ChecklistButton entity={entity} checklistComponent={checklistComponent} />
        <View fill>{children}</View>
      </View>
    );
  }
}

interface GetIsWideCardLayoutProps {
  mode: LayoutMode;
  subtype: API.ViewSubtype | null;
}

function getIsWideCardLayout({ mode, subtype }: GetIsWideCardLayoutProps) {
  switch (subtype) {
    case 'BOARD':
      return false;
    default:
      return ['split-workflow', 'workflow'].includes(mode);
  }
}

function useVisibleComponents({
  workflowTemplate,
  viewTemplate,
  entity,
}: {
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate: API.ViewTemplateFragment;
  entity: Entity;
}): [API.AnyComponent, API.AnyComponent[]] {
  return useMemo(() => {
    const filtered = workflowTemplate.components
      .filter(component => {
        const viewOptions = viewTemplate.componentsViewOptions?.find(
          options => options.componentId === component.id,
        );
        const field = entity.fields.get(component.id);
        const needsFieldValue = isInputComponent(component) || isManagingInputComponent(component);
        const isFieldEmpty = isNil(field) || field.isEmpty;
        const isComponentHidden = getIsComponentHidden(viewTemplate, viewOptions, component);
        return !isComponentHidden && (!isFieldEmpty || !needsFieldValue);
      })
      .map(component => getComponentWithOverrides(component, viewTemplate));
    const [firstComponent, ...otherComponents] = filtered;
    return [firstComponent, otherComponents];
  }, [workflowTemplate, viewTemplate, entity]);
}

function getChecklistComponent({
  viewTemplate,
  workflowTemplate,
}: {
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplate: API.WorkflowTemplateFragment;
}): ChecklistTagComponent | undefined {
  if (viewTemplate.subtype !== 'LIST') {
    return undefined;
  }
  const checklistComponent = workflowTemplate.components
    .filter(isChecklistTagComponent)
    .find(component => component.id === viewTemplate.checklistTagComponentId);
  return checklistComponent;
}

interface ChecklistButtonProps {
  entity: Entity;
  checklistComponent: ChecklistTagComponent;
}

function ChecklistButton({ entity, checklistComponent }: ChecklistButtonProps) {
  const { updateWorkflowEntity, loading } = useUpdateWorkflowEntity();
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(false);
  const colors = useContext(ColorThemeContext);
  const field = getTagField(entity, checklistComponent);
  const { checkedOption, uncheckedOption } = checklistComponent.checklistOptions;
  const isCompleted = field?.tagIds?.includes(checkedOption);
  // optimistically invert completed state while request is in flight
  const completed = loading ? !isCompleted : isCompleted;
  const color = completed || isHovered ? colors.accent : colors.accentInactive;
  const iconName = completed ? 'task-filled' : 'task';
  const label = getComponentLabel(checklistComponent);
  const opacity = completed ? (isHovered ? 0.9 : 1) : isHovered ? 1 : 0.7;

  const onPress = () => {
    updateWorkflowEntity(entity.id, {
      [checklistComponent.id]: isCompleted ? [uncheckedOption] : [checkedOption],
    });
  };

  return (
    <View center paddingLeft={1} paddingTop={0.5} className={CardStyles.checklistButton}>
      <HoverTooltip tooltip={i18n('collectionView.listView.checkList.toggleTooltip', { label })}>
        <MBHover onHoverStart={onHoverStart} onHoverEnd={onHoverEnd} style={{ opacity }}>
          <IconButtonV2 size={grid(4.5)} name={iconName} color={color} onPress={onPress} />
        </MBHover>
      </HoverTooltip>
    </View>
  );
}

function getTagField(entity: Entity, checklistComponent: ChecklistTagComponent): TagField | null {
  const field = entity.allFields.get(checklistComponent.id);
  if (field instanceof TagField) {
    return field;
  } else {
    return null;
  }
}

interface CardCollectionViewComponentProps {
  component: API.AnyComponent;
  entity: Entity;
  workflowTemplateId: string;
  fieldStyles: FieldStyles;
  showLabel: boolean;
  analytics?: CollectionViewAnalytics;
  canUpdateCard: boolean;
  isRedacted: boolean;
  isCollapsed: boolean;
}

function CardCollectionViewComponent({
  component,
  entity,
  workflowTemplateId,
  fieldStyles,
  showLabel,
  analytics,
  canUpdateCard,
  isRedacted = false,
  isCollapsed = false,
}: CardCollectionViewComponentProps) {
  const colors = useContext(ColorThemeContext);
  const field = entity.fields.get(component.id);
  const componentIconName = getComponentIcon(component);
  const componentLabel = getComponentLabel(component);

  return (
    <View
      className={[
        CardStyles.component,
        isRedacted && CardStyles.redacted,
        showLabel && CardStyles.showLabel,
        isCollapsed && CardStyles.collapsed,
      ]}
    >
      {!isEmpty(componentIconName) && (
        <View paddingTop={0.3}>
          <MBIconV2 name={componentIconName as MBIconV2Name} size={grid(4)} color={colors.accent} />
        </View>
      )}
      <View paddingHorizontal={1} fill>
        {showLabel && isString(componentLabel) && !isEmpty(componentLabel) && (
          <MBBlockLabel label={componentLabel} />
        )}
        <CardCollectionViewField
          field={field}
          workflowTemplateId={workflowTemplateId}
          entityId={entity.id}
          component={component}
          fieldStyles={fieldStyles}
          isCollapsed={isCollapsed}
          showLabel={showLabel}
          canUpdateCard={canUpdateCard}
          analytics={analytics}
        />
      </View>
    </View>
  );
}
